import { useState } from 'react';

interface StudyGuideCardProps {
  unit: {
    unitNumber: number;
    unitTitle: string;
    keyFormulas: string[];
    vocabulary: { term: string; definition: string }[];
    importantRules: string[];
    exampleProblems: { problem: string; solution: string }[];
  };
}

export default function StudyGuideCard({ unit }: StudyGuideCardProps) {
  const [isPrinting, setIsPrinting] = useState(false);

  const handlePrint = () => {
    setIsPrinting(true);
    setTimeout(() => {
      window.print();
      setIsPrinting(false);
    }, 100);
  };

  return (
    <>
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            .print-content, .print-content * {
              visibility: visible;
            }
            .print-content {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }
            .no-print {
              display: none !important;
            }
          }
        `}
      </style>
      <div className={`bg-white rounded-lg border-2 border-gray-200 overflow-hidden ${isPrinting ? 'print-content' : ''}`}>
        <div className="bg-gradient-to-r from-teal-500 to-emerald-500 px-6 py-4">
          <h3 className="text-xl font-bold text-white">
            Unit {unit.unitNumber}: {unit.unitTitle}
          </h3>
          <p className="text-teal-50 text-sm mt-1">Quick Reference Guide</p>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Key Formulas */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-calculator-line text-xl text-teal-600"></i>
                <h4 className="font-bold text-gray-800">Key Formulas</h4>
              </div>
              <div className="space-y-2">
                {unit.keyFormulas.map((formula, idx) => (
                  <div key={idx} className="bg-teal-50 px-3 py-2 rounded-md border border-teal-200">
                    <p className="text-sm text-gray-700 font-mono">{formula}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Vocabulary */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-book-open-line text-xl text-emerald-600"></i>
                <h4 className="font-bold text-gray-800">Essential Vocabulary</h4>
              </div>
              <div className="space-y-2">
                {unit.vocabulary.map((item, idx) => (
                  <div key={idx} className="bg-emerald-50 px-3 py-2 rounded-md border border-emerald-200">
                    <p className="text-sm font-semibold text-gray-800">{item.term}</p>
                    <p className="text-xs text-gray-600 mt-1">{item.definition}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Important Rules */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-lightbulb-line text-xl text-amber-600"></i>
                <h4 className="font-bold text-gray-800">Important Rules</h4>
              </div>
              <div className="space-y-2">
                {unit.importantRules.map((rule, idx) => (
                  <div key={idx} className="flex items-start gap-2 bg-amber-50 px-3 py-2 rounded-md border border-amber-200">
                    <span className="text-amber-600 font-bold text-sm mt-0.5">•</span>
                    <p className="text-sm text-gray-700 flex-1">{rule}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Example Problems */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-file-list-3-line text-xl text-blue-600"></i>
                <h4 className="font-bold text-gray-800">Example Problems</h4>
              </div>
              <div className="space-y-3">
                {unit.exampleProblems.map((example, idx) => (
                  <div key={idx} className="bg-blue-50 px-3 py-2 rounded-md border border-blue-200">
                    <p className="text-sm font-semibold text-gray-800 mb-1">Problem:</p>
                    <p className="text-sm text-gray-700 mb-2">{example.problem}</p>
                    <p className="text-sm font-semibold text-gray-800 mb-1">Solution:</p>
                    <p className="text-sm text-gray-700 font-mono">{example.solution}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Print Button */}
          <div className="mt-6 pt-6 border-t border-gray-200 no-print">
            <button
              onClick={handlePrint}
              className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-teal-600 hover:to-emerald-600 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-printer-line text-xl"></i>
              Print This Unit
            </button>
          </div>
        </div>
      </div>
    </>
  );
}